import {
	buildContentAri,
	getContentGraphNodeLabel,
	type BylineContentType,
} from '../utils/contentAri.ts';

import type {
	useIncomingLinksQuery$data,
	useIncomingLinksQuery$variables,
} from '../hooks/__generated__/useIncomingLinksQuery.graphql.ts';

export type IncomingLinksSsrStatus = 'present' | 'empty' | 'unavailable';

// `count` (sampled rows, at most `INCOMING_LINKS_SAMPLE_LIMIT`) exists only on the 'present' variant so
// the SSR placeholder can add it to the other sources when deciding whether the page reaches the
// byline's row minimum. CCINT-9359
export type IncomingLinksSsrState = Readonly<
	| { contentId: string; status: 'present'; count: number }
	| { contentId: string; status: 'empty' | 'unavailable' }
>;

export type IncomingLinksSampleUnavailableReason =
	| 'invalid-input'
	| 'malformed-response'
	| 'query-error';

// The preload derives presence from a bounded sample; only the row count leaves this layer, but the
// underlying query (and its cached entry) holds the source ARIs like any other preloaded query.
export type IncomingLinksSampleResult =
	| Readonly<{ status: 'available'; count: number }>
	| Readonly<{ status: 'unavailable'; reason: IncomingLinksSampleUnavailableReason }>;

// The placeholder only needs to distinguish zero, one, two, and three or more rows.
export const INCOMING_LINKS_SAMPLE_LIMIT = 3;

const SOURCE_COLUMN = 'source';

export function createIncomingLinksSsrState(
	contentId: string,
	status: 'empty' | 'unavailable',
): IncomingLinksSsrState;
export function createIncomingLinksSsrState(
	contentId: string,
	status: 'present',
	count: number,
): IncomingLinksSsrState;
export function createIncomingLinksSsrState(
	contentId: string,
	status: IncomingLinksSsrStatus,
	count?: number,
): IncomingLinksSsrState {
	if (status === 'present') {
		return Object.freeze({ contentId, status, count: count ?? 0 });
	}
	return Object.freeze({ contentId, status });
}

const buildIncomingLinksCypher = (targetAri: string, contentType?: BylineContentType): string => {
	const label = getContentGraphNodeLabel(contentType);
	return [
		`MATCH (source)-[:content_referenced_entity]->(content:${label} {ari: "${targetAri}"})`,
		'RETURN source',
		'ORDER BY source.lastUpdated DESC',
		'LIMIT 1000',
	].join('\n');
};

// Shared by the preloader (network) and the cache-only reader so both key the GraphQL cache on a
// byte-identical query and variables. Returns `undefined` for input that must not be queried, which
// the reader treats as "skip" and the preloader treats as "invalid-input". Pages and blog posts
// are distinct graph node types, so `contentType` (undefined means page) is part of that key.
export const buildIncomingLinksSampleVariables = (
	cloudId: string,
	contentId: string,
	contentType?: BylineContentType,
): useIncomingLinksQuery$variables | undefined => {
	if (!cloudId.trim() || !/^[1-9]\d*$/.test(contentId)) {
		return undefined;
	}

	let targetAri: string;
	try {
		targetAri = buildContentAri(cloudId, contentId, contentType);
	} catch {
		return undefined;
	}

	return {
		cypherQuery: buildIncomingLinksCypher(targetAri, contentType),
		first: 50,
		after: null,
	};
};

// Pure normalization of the bounded sample. Defensive against a malformed shape because the response
// is an untyped Cypher projection. Only the row count leaves this function; presence is any sampled
// source row.
export const normalizeIncomingLinksSampleData = (
	data: useIncomingLinksQuery$data | null | undefined,
): IncomingLinksSampleResult => {
	const edges = data?.graphStore_cypherQueryV2?.edges;
	if (!edges || edges.length > 50) {
		return { status: 'unavailable', reason: 'malformed-response' };
	}

	for (const edge of edges) {
		const columns = edge.node.columns;
		if (columns.length !== 1) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}

		const column = columns[0];
		if (
			column.key !== SOURCE_COLUMN ||
			column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode' ||
			typeof column.value.id !== 'string' ||
			!column.value.id.startsWith('ari:')
		) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}
	}

	return { status: 'available', count: Math.min(edges.length, INCOMING_LINKS_SAMPLE_LIMIT) };
};

// Maps a query response (from the network preload or the hydrated cache) to the terminal SSR state.
// A malformed sample is `unavailable`; otherwise presence is any sampled row.
export const normalizeIncomingLinksSsrState = (
	contentId: string,
	data: useIncomingLinksQuery$data | null | undefined,
): IncomingLinksSsrState => {
	const result = normalizeIncomingLinksSampleData(data);
	if (result.status === 'unavailable') {
		return createIncomingLinksSsrState(contentId, 'unavailable');
	}
	return result.count > 0
		? createIncomingLinksSsrState(contentId, 'present', result.count)
		: createIncomingLinksSsrState(contentId, 'empty');
};
